import { useState, useMemo, useEffect } from "react";
import { Panel, fmt, fmtDate } from "../Dashboard/DashboardPrimitives";
import { useAuth } from "src/context/AuthProvider";
import { MdRefresh, MdLink, MdOutlineAccountBalance } from "react-icons/md";
import {
    TbCircleCheck,
    TbLink,
    TbUnlink,
    TbFileInvoice,
    TbAlertCircle,
    TbUserCheck,
    TbChevronDown,
    TbSearch,
    TbInfoCircle,
    TbSettings,
    TbCreditCard,
} from "react-icons/tb";
import { useSumupPayments } from "src/hooks/useSumup";
import { CardPaymentsTable, cardPaymentMatchesSearch } from "./CardPaymentsTable";
import { FaUserInjured } from "react-icons/fa6";
import { PiHandArrowDown } from "react-icons/pi";
import { useFinApi, FinApiTransaction } from "src/hooks/useFinApi";
import { useApi } from "src/hooks/useApi";
import { useTranslation, Trans } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { openInvoiceMatchesSearch } from "src/utils/invoiceSearch";
import { canSeeBank as canSeeBankFor, canReconcileBank } from "src/utils/roles";
import Popup from "src/components/Popup/Popup";
import PageHeader from "src/components/PageHeader/PageHeader";
import RosterSubBar from "src/components/PageHeader/RosterSubBar";
import BankFilterBar, { BankFilters, BankStatusFilter, DEFAULT_BANK_FILTERS } from "./BankFilterBar";
import TablePagination from "src/components/TablePagination/TablePagination";
import { useClientPagination } from "src/hooks/useClientPagination";
import "src/components/Popup/popup.scss";
import "../../styles/tables.scss";
import "../Dashboard/dashboard.scss";
import "./bank.scss";

const BANK_ACCENT = "#1565c0";

interface OpenInvoice {
    id: string;
    readableId: string;
    amount: number;
    remaining?: number;
    recipientId?: string;
    recipientName?: string;
    patientId?: string;
    patientName?: string;
}

// readableIds are generated from a charset that includes both 'O'/'0' and 'I'/'1' — visually
// identical in most fonts. Mirrors normalizeForMatch in banksApiController.ts so the client-side
// suggestion ranking agrees with what the backend's own auto-reconcile would pick.
const normalizeForMatch = (s: string) => s.toLowerCase().replace(/o/g, "0").replace(/i/g, "1");

/** One incoming transaction, normalised to the shape the rest of this component works with
 *  (pending vs. settled transactions arrive in slightly different shapes upstream). */
interface UnifiedTx {
    id: string;
    amount: number;
    currency: string;
    purpose: string | null;
    counterpartName: string | null;
    date: string;
    matchedInvoiceIds: string[];
    matchedInvoices: FinApiTransaction["matchedInvoices"];
    matchedBy: "auto" | "manual" | null;
    ignored: boolean;
    possibleDuplicateInvoice?: FinApiTransaction["possibleDuplicateInvoice"];
    pending: boolean;
    /** Which doctor's connected account this came from — see FinApiTransaction. */
    ownerDoctorId: string;
    ownerDoctorName: string | null;
}

type StatusCategory = "auto" | "manual" | "unmatched" | "ignored" | "pending";

/** An incoming SumUp payout (batched card payments). Its card payments are already booked one by
 *  one on their invoices, so this transfer must not be matched again — the chip says so. */
function isSumupPayout(tx: UnifiedTx): boolean {
    // Same rule as the backend's isSumupPayout (banksApiController) — those are never auto-matched.
    return tx.amount > 0 && /sum\s?up/i.test(tx.counterpartName ?? "");
}

function statusOf(tx: UnifiedTx): StatusCategory {
    if (tx.pending) return "pending";
    if (tx.ignored) return "ignored";
    if (tx.matchedInvoiceIds.length > 0) return tx.matchedBy === "manual" ? "manual" : "auto";
    return "unmatched";
}

interface Candidate {
    id: string;
    readableId: string;
    recipientName?: string;
    patientName?: string;
    /** The figure used for scoring/matching — the invoice's remaining balance. */
    amount: number;
    /** Invoice total — always shown as the primary figure. */
    totalAmount: number;
    /** Already paid so far — shown as secondary context. */
    paidAmount: number;
    score: number;
}

/** Ranks a candidate invoice against a transaction — mirrors the signals the backend's own
 *  auto-reconcile uses (readableId in purpose, amount match) plus a name-similarity bonus the
 *  backend doesn't need (it never guesses by name). Client-side only — no new endpoint. */
function scoreCandidate(tx: UnifiedTx, readableId: string, candidateAmount: number, recipientName?: string, patientName?: string): number {
    let score = 0;
    const purposeNorm = normalizeForMatch(tx.purpose ?? "");
    const ridNorm = normalizeForMatch(readableId);
    if (ridNorm && purposeNorm.includes(ridNorm)) score += 100;

    const diff = Math.abs(Math.abs(tx.amount) - candidateAmount);
    if (diff < 0.01) score += 60;
    else if (diff < 2) score += 30;

    const counterpart = (tx.counterpartName ?? "").toLowerCase();
    if (counterpart) {
        if (recipientName && counterpart.includes(recipientName.toLowerCase())) score += 20;
        if (patientName && counterpart.includes(patientName.toLowerCase())) score += 20;
    }
    return score;
}

const CANDIDATE_PREVIEW = 3;

/** Manual-match popup — a modal dialog for assigning a transaction to an invoice, rather than
 *  a persistent inline side panel. Reuses the same candidate-ranking/search logic, just
 *  presented as a Popup (matches the rest of the app's match/confirm dialogs). */
function BankMatchPopup({
    tx,
    onAssign,
    onMarkReviewed,
    onClose,
}: {
    tx: UnifiedTx;
    onAssign: (invoiceId: string, amount: number) => Promise<void>;
    onMarkReviewed: () => Promise<void>;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    // Only the invoices of the doctor whose account received this payment (each doctor has
    // their own account) — the backend rejects a match to any other doctor's invoice.
    const { data: openInvoices, loading } = useApi<OpenInvoice[]>(`/invoices/open?bankDoctorId=${encodeURIComponent(tx.ownerDoctorId)}`);

    const [query, setQuery] = useState("");
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [amount, setAmount] = useState(Math.abs(tx.amount).toFixed(2));
    const [showAll, setShowAll] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const candidates: Candidate[] = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (openInvoices ?? [])
            .filter((inv) => !q || openInvoiceMatchesSearch(inv, query))
            .map((inv) => {
                const amt = inv.remaining ?? inv.amount;
                const paid = +(inv.amount - amt).toFixed(2);
                return {
                    id: inv.id, readableId: inv.readableId, recipientName: inv.recipientName, patientName: inv.patientName,
                    amount: amt, totalAmount: inv.amount, paidAmount: paid,
                    score: scoreCandidate(tx, inv.readableId, amt, inv.recipientName, inv.patientName),
                };
            })
            .sort((a, b) => b.score - a.score || a.readableId.localeCompare(b.readableId, undefined, { numeric: true }));
    }, [tx, openInvoices, query]);

    // Pre-select the top suggestion once it's a genuine signal (readableId or amount hit),
    // never a bare alphabetical fallback — the doctor should never assign a guess by accident.
    useEffect(() => {
        setSelectedId(candidates[0] && candidates[0].score > 0 ? candidates[0].id : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openInvoices]);

    const visible = showAll ? candidates : candidates.slice(0, CANDIDATE_PREVIEW);
    const parsedAmount = parseFloat(amount);
    const validAmount = !Number.isNaN(parsedAmount) && parsedAmount > 0;

    const handleAssign = async () => {
        if (!selectedId || !validAmount) return;
        setSubmitting(true);
        try {
            await onAssign(selectedId, parsedAmount);
        } finally {
            setSubmitting(false);
        }
    };

    const handleMarkReviewed = async () => {
        setSubmitting(true);
        try {
            await onMarkReviewed();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Popup
            title={t("finapi.matchPopupTitle")}
            onClose={onClose}
            className="finapi-match-popup"
            actions={[
                { label: t("popup.cancel"), variant: "default", disabled: submitting, onClick: onClose },
                { label: t("finapi.markReviewed"), variant: "default", disabled: submitting, onClick: handleMarkReviewed },
                { label: submitting ? t("finapi.matching") : t("finapi.manualMatch"), variant: "primary", disabled: !selectedId || !validAmount || submitting, onClick: handleAssign },
            ]}
            message={
                <fieldset className="finapi-suggest__body" disabled={submitting}>
                    <div className="finapi-match-tx">
                        <div className="finapi-match-tx__info">
                            <span className="finapi-match-tx__name">{tx.counterpartName ?? t("finapi.unknownCounterpart")}</span>
                            {tx.purpose && <span className="finapi-match-tx__purpose">{tx.purpose}</span>}
                        </div>
                        <span className="finapi-match-tx__amount">+ {fmt(tx.amount)}</span>
                    </div>
                    {tx.ownerDoctorName && (
                        <p className="finapi-match-owner">
                            <TbInfoCircle aria-hidden />
                            {t("finapi.matchOwnerHint", { name: tx.ownerDoctorName })}
                        </p>
                    )}

                    <div className="finapi-suggest__search">
                        <TbSearch />
                        <input
                            placeholder={t("finapi.manualMatchSearch")}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </div>

                    <span className="finapi-suggest__label">{t("finapi.possibleInvoices")}</span>
                    <div className="finapi-match-list finapi-suggest__list">
                        {loading && <span className="finapi-match-list__empty">{t("finapi.loading")}</span>}
                        {!loading && candidates.length === 0 && (
                            <span className="finapi-match-list__empty">{t("finapi.manualMatchNone")}</span>
                        )}
                        {!loading && visible.map((c, i) => {
                            const isSelected = selectedId === c.id;
                            return (
                                <div key={c.id} className={`finapi-match-invoice${isSelected ? " finapi-match-invoice--selected" : ""}`}>
                                    <button
                                        type="button"
                                        className="finapi-match-invoice__toggle"
                                        onClick={() => setSelectedId(c.id)}
                                    >
                                        <span className={`finapi-suggest__radio${isSelected ? " finapi-suggest__radio--on" : ""}`} aria-hidden />
                                        <div className="finapi-match-invoice__main">
                                            <div className="finapi-match-invoice__head">
                                                <TbFileInvoice className="finapi-match-invoice__icon" aria-hidden />
                                                <span className="finapi-match-invoice__id">{c.readableId}</span>
                                                {i === 0 && c.score > 0 && (
                                                    <span className="finapi-suggest__best-badge">{t("finapi.bestMatch")}</span>
                                                )}
                                            </div>
                                            <div className="finapi-match-invoice__people">
                                                {c.recipientName && (
                                                    <span className="finapi-match-invoice__person finapi-match-invoice__person--account">
                                                        <PiHandArrowDown className="finapi-match-invoice__person-icon" aria-hidden />
                                                        <span className="finapi-match-invoice__person-label">{t("finapi.accountHolder")}</span>
                                                        <span className="finapi-match-invoice__person-name">{c.recipientName}</span>
                                                    </span>
                                                )}
                                                {c.patientName && (
                                                    <span className="finapi-match-invoice__person finapi-match-invoice__person--billed">
                                                        <FaUserInjured className="finapi-match-invoice__person-icon" aria-hidden />
                                                        <span className="finapi-match-invoice__person-label">{t("finapi.billedPatient")}</span>
                                                        <span className="finapi-match-invoice__person-name">{c.patientName}</span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <span className="finapi-match-invoice__amount">
                                            <span className="finapi-match-invoice__amount-primary">{fmt(c.totalAmount)}</span>
                                            <span className="finapi-match-invoice__amount-secondary">
                                                {t("finapi.paidLabel")} {fmt(c.paidAmount)}
                                            </span>
                                        </span>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    {!loading && candidates.length > CANDIDATE_PREVIEW && (
                        <button type="button" className="finapi-suggest__more" onClick={() => setShowAll((v) => !v)}>
                            {showAll ? t("finapi.showLessInvoices") : t("finapi.showMoreInvoices")}
                            <TbChevronDown className={showAll ? "finapi-suggest__more-chevron--up" : undefined} />
                        </button>
                    )}

                    <div className="finapi-suggest__amount-row">
                        <span className="finapi-suggest__label">{t("finapi.amountToAddLabel")}</span>
                        <div className="finapi-match-invoice__alloc finapi-suggest__amount-input">
                            <span className="finapi-match-invoice__alloc-currency">€</span>
                            <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
                        </div>
                    </div>
                    <p className="finapi-suggest__effect finapi-suggest__effect--add">
                        {t("finapi.effectAdd", { amount: fmt(validAmount ? parsedAmount : 0) })}
                    </p>
                    <p className="finapi-suggest__hint">{t("finapi.noMatchHint")}</p>
                </fieldset>
            }
        />
    );
}

const STATUS_TAB_VALUES: BankStatusFilter[] = ["all", "auto", "unmatched", "reviewed"];

export default function Bank() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const role = user?.doctor?.userRights;
    const canSeeBank = canSeeBankFor(user?.doctor);
    const canReconcile = canReconcileBank(user?.doctor);
    // Connect/sync/disconnect are never delegable (requireBankManage on the backend has no
    // override) — Doctor/Admin only, matching that exactly rather than mirroring permBank.
    const canManageBank = role === 2 || role === 3;

    // Avoid navigation during render (can happen during logout / auth changes) — mirrors the
    // same guard pattern Charges.tsx uses for admin-only access.
    useEffect(() => {
        if (!canSeeBank) navigate("/main", { replace: true });
    }, [canSeeBank, navigate]);

    const {
        loading, connected, connections, accounts, bankName, recentTransactions, pendingTransactions,
        syncing, syncError, sync, manualMatch, unmatch, lastSyncAt,
        markIgnored, unignore, notifyPatientDuplicate, refetch,
    } = useFinApi();
    // Several doctors in the same praxis can each have their own connected account — only show
    // whose-account-is-this once there's actually more than one, so the common single-doctor
    // case stays exactly as it already looked.
    const multiAccount = connections.length > 1;

    // Dashboard's "stale unmatched transactions" todo links here with a pre-applied filter
    // (state.statusTab) — same navigate(path, { state }) convention as the Invoices page's
    // viewKey. Falls back to "all" for a plain nav-menu visit, which has no state at all.
    const initialStatusTab = STATUS_TAB_VALUES.includes(location.state?.statusTab)
        ? (location.state.statusTab as BankStatusFilter)
        : "all";
    const [statusTab, setStatusTab] = useState<BankStatusFilter>(initialStatusTab);
    // Dashboard's "double payment" todo links here with a pre-applied search (the invoice's
    // readableId, which duplicate bank transfers almost always carry in their purpose text) —
    // same state convention as statusTab above, so the doctor lands directly on the two matched
    // transactions instead of a dead-end invoice page with no bank-match data at all.
    const initialSearch = typeof location.state?.search === "string" ? location.state.search : "";
    const [search, setSearch] = useState(initialSearch);
    const [bankFilters, setBankFilters] = useState<BankFilters>(DEFAULT_BANK_FILTERS);
    const [matchingTx, setMatchingTx] = useState<UnifiedTx | null>(null);
    const [unmatching, setUnmatching] = useState<string | null>(null);
    const [ignoring, setIgnoring] = useState<string | null>(null);
    const [notifyingDuplicate, setNotifyingDuplicate] = useState<string | null>(null);

    // "Kartenzahlungen" — SumUp card payments from the patient portal. Its own tab next to the
    // bank-feed tabs, and shown on its own when no bank account is connected.
    const [view, setView] = useState<"bank" | "card">(location.state?.view === "card" ? "card" : "bank");
    const { available: cardAvailable, payments: cardPayments } = useSumupPayments(canSeeBank);
    const filteredCardPayments = cardPayments.filter((p) => cardPaymentMatchesSearch(p, search));
    const cardShowAccount = new Set(cardPayments.map((p) => p.account)).size > 1;
    const selectStatusTab = (tab: BankStatusFilter) => { setView("bank"); setStatusTab(tab); };

    const unifiedTxs: UnifiedTx[] = useMemo(() => {
        const pending: UnifiedTx[] = pendingTransactions.map((tx) => ({
            id: tx.id, amount: tx.amount, currency: tx.currency, purpose: tx.purpose, counterpartName: tx.counterpartName,
            date: tx.valueDate, matchedInvoiceIds: [], matchedInvoices: [], matchedBy: null, ignored: false, pending: true,
            ownerDoctorId: tx.ownerDoctorId, ownerDoctorName: tx.ownerDoctorName,
        }));
        const incoming: UnifiedTx[] = recentTransactions.map((tx) => ({
            id: tx.id, amount: tx.amount, currency: tx.currency, purpose: tx.purpose, counterpartName: tx.counterpartName,
            date: tx.bookingDate, matchedInvoiceIds: tx.matchedInvoiceIds, matchedInvoices: tx.matchedInvoices ?? [], matchedBy: tx.matchedBy, ignored: tx.ignored,
            possibleDuplicateInvoice: tx.possibleDuplicateInvoice, pending: false,
            ownerDoctorId: tx.ownerDoctorId, ownerDoctorName: tx.ownerDoctorName,
        }));
        return [...pending, ...incoming].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    }, [pendingTransactions, recentTransactions]);

    const autoCount = unifiedTxs.filter((tx) => statusOf(tx) === "auto").length;
    const unmatchedCount = unifiedTxs.filter((tx) => statusOf(tx) === "unmatched").length;
    const reviewedCount = unifiedTxs.filter((tx) => statusOf(tx) === "manual" || statusOf(tx) === "ignored").length;

    const filteredTxs = unifiedTxs.filter((tx) => {
        const status = statusOf(tx);
        if (statusTab === "auto" && status !== "auto") return false;
        if (statusTab === "unmatched" && status !== "unmatched") return false;
        if (statusTab === "reviewed" && status !== "manual" && status !== "ignored") return false;

        const q = search.trim().toLowerCase();
        if (q) {
            const matchesText = (tx.counterpartName ?? "").toLowerCase().includes(q) || (tx.purpose ?? "").toLowerCase().includes(q);
            // Dashboard's "double payment" todo searches by the matched invoice's readableId,
            // which often doesn't appear in the transaction's own purpose/reference text —
            // without this, that link lands on an empty "Keine Transaktionen" result.
            const matchesInvoice = (tx.matchedInvoices ?? []).some((inv) => inv.readableId.toLowerCase().includes(q));
            if (!matchesText && !matchesInvoice) return false;
        }

        const absAmount = Math.abs(tx.amount);
        if (bankFilters.amountMin !== "" && absAmount < parseFloat(bankFilters.amountMin)) return false;
        if (bankFilters.amountMax !== "" && absAmount > parseFloat(bankFilters.amountMax)) return false;
        if (bankFilters.dateFrom && tx.date < bankFilters.dateFrom) return false;
        if (bankFilters.dateTo && tx.date > bankFilters.dateTo) return false;
        if (bankFilters.payer && !(tx.counterpartName ?? "").toLowerCase().includes(bankFilters.payer.toLowerCase())) return false;
        if (bankFilters.purpose && !(tx.purpose ?? "").toLowerCase().includes(bankFilters.purpose.toLowerCase())) return false;

        return true;
    });

    const {
        page: txPage, setPage: setTxPage, pageSize, setPageSize, totalPages, pagedItems: pageTxs, totalCount: filteredCount, PAGE_SIZES,
    } = useClientPagination(filteredTxs, [statusTab, search, bankFilters]);

    const handleAssign = async (invoiceId: string, amount: number) => {
        if (!matchingTx) return;
        await manualMatch(matchingTx.id, [{ invoiceId, amount }], matchingTx.ownerDoctorId);
        setMatchingTx(null);
        await refetch();
    };

    const handleMarkReviewed = async () => {
        if (!matchingTx) return;
        await markIgnored(matchingTx.id, matchingTx.ownerDoctorId);
        setMatchingTx(null);
        await refetch();
    };

    const handleUnmatch = async (tx: UnifiedTx) => {
        setUnmatching(tx.id);
        try {
            await unmatch(tx.id, tx.ownerDoctorId);
            await refetch();
        } finally {
            setUnmatching(null);
        }
    };

    const handleIgnore = async (tx: UnifiedTx) => {
        setIgnoring(tx.id);
        try {
            await markIgnored(tx.id, tx.ownerDoctorId);
            await refetch();
        } finally {
            setIgnoring(null);
        }
    };

    const handleUnignore = async (tx: UnifiedTx) => {
        setIgnoring(tx.id);
        try {
            await unignore(tx.id, tx.ownerDoctorId);
            await refetch();
        } finally {
            setIgnoring(null);
        }
    };

    const handleNotifyDuplicate = async (tx: UnifiedTx) => {
        if (!tx.possibleDuplicateInvoice) return;
        setNotifyingDuplicate(tx.id);
        try {
            await notifyPatientDuplicate(tx.id, tx.possibleDuplicateInvoice.id, tx.ownerDoctorId);
            await refetch();
        } finally {
            setNotifyingDuplicate(null);
        }
    };

    const statusChip = (tx: UnifiedTx) => {
        if (tx.pending) return { label: t("finapi.pendingLabel"), cls: "pending" };
        if (tx.ignored) return { label: t("finapi.statusReviewed"), cls: "reviewed" };
        if (tx.matchedInvoiceIds.length > 0) return { label: t("finapi.statusMatched"), cls: "matched" };
        return { label: t("finapi.statusOpen"), cls: "open" };
    };

    // `accounts` is only the first connection's (single-account back-compat shape) — sum every
    // connected doctor's accounts instead.
    const totalBalance = (connections.length ? connections.flatMap((c) => c.accounts) : accounts)
        .reduce((s, a) => s + (a.balance ?? 0), 0);

    if (!canSeeBank) return null;

    return (
        <div className="page page--roster bank-page">
            <div className="roster-body">
                <PageHeader
                    introOnly
                    sticky
                    loading={loading}
                    eyebrow={t("nav.bank")}
                    eyebrowIcon={<MdOutlineAccountBalance />}
                    eyebrowExtras={!loading && connected ? (
                        multiAccount
                            ? [t("finapi.multiAccountSummary", { count: connections.length })]
                            : [bankName ?? t("finapi.panelTitle"), ...(accounts[0]?.iban ? [accounts[0].iban] : [])]
                    ) : undefined}
                    title={t("bank.title")}
                    count={!loading && connected ? fmt(totalBalance) : undefined}
                    lead={
                        loading ? undefined : !connected ? t("finapi.dashboardNudge") : (
                            <Trans
                                i18nKey="bank.leadConnected"
                                values={{ total: unifiedTxs.length, unmatched: unmatchedCount, auto: autoCount, reviewed: reviewedCount }}
                                components={{ strong: <strong />, em: <em /> }}
                            />
                        )
                    }
                    search={connected || cardAvailable ? search : undefined}
                    setSearch={connected || cardAvailable ? setSearch : undefined}
                    placeholder={t("finapi.searchPlaceholder")}
                    primaryAction={!loading && !connected ? {
                        label: <><MdLink /> {t("finapi.connectBtn")}</>,
                        onClick: () => navigate("/settings?section=bankpayments"),
                    } : undefined}
                    secondaryAction={!loading && connected ? (
                        <>
                            {canManageBank && (
                                <button type="button" className="page-header__cta page-header__cta--outline" onClick={sync} disabled={syncing}>
                                    <MdRefresh className={syncing ? "spin" : undefined} /> {t("finapi.sync")}
                                </button>
                            )}
                            {canManageBank && (
                                <button
                                    type="button"
                                    className="page-header__cta page-header__cta--outline page-header__cta--settings"
                                    onClick={() => navigate("/settings?section=bankpayments")}
                                >
                                    <TbSettings /> {t("finapi.goToSettings")}
                                </button>
                            )}
                        </>
                    ) : undefined}
                    accentColor={BANK_ACCENT}
                />

                {syncing    && <p className="finapi-sync-notice">{t("finapi.syncing")}</p>}
                {unmatching && <p className="finapi-sync-notice">{t("finapi.unmatching")}</p>}
                {syncError && (
                    <div className="finapi-sync-error">
                        <TbInfoCircle className="finapi-sync-error__icon" />
                        <div className="finapi-sync-error__body">
                            <p className="finapi-sync-error__title">{t("finapi.syncFailedTitle")}</p>
                            <p className="finapi-sync-error__hint">{t("finapi.syncFailedHint")}</p>
                            {lastSyncAt && (
                                <p className="finapi-sync-error__last">
                                    {t("finapi.syncFailedLastSync", { datetime: new Date(lastSyncAt).toLocaleString("de-DE") })}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {!loading && connected && (
                    <>
                        <RosterSubBar
                            loading={loading}
                            accentColor={BANK_ACCENT}
                            tabs={[
                                { key: "all", label: t("finapi.tabAll"), count: unifiedTxs.length, active: view === "bank" && statusTab === "all", onClick: () => selectStatusTab("all") },
                                { key: "auto", label: t("finapi.statAutoMatched"), count: autoCount, active: view === "bank" && statusTab === "auto", onClick: () => selectStatusTab("auto") },
                                { key: "unmatched", label: t("finapi.statUnmatched"), count: unmatchedCount, active: view === "bank" && statusTab === "unmatched", onClick: () => selectStatusTab("unmatched") },
                                { key: "reviewed", label: t("finapi.statReviewed"), count: reviewedCount, active: view === "bank" && statusTab === "reviewed", onClick: () => selectStatusTab("reviewed") },
                                ...(cardAvailable
                                    ? [{ key: "card", label: t("sumup.tabCardPayments"), count: cardPayments.length, active: view === "card", onClick: () => setView("card") }]
                                    : []),
                            ]}
                            subRightActions={view === "bank" ? (
                                <BankFilterBar
                                    filters={bankFilters}
                                    onChange={setBankFilters}
                                    statusTab={statusTab}
                                    setStatusTab={selectStatusTab}
                                    counts={{ all: unifiedTxs.length, auto: autoCount, unmatched: unmatchedCount, reviewed: reviewedCount }}
                                />
                            ) : undefined}
                        />

                        {view === "card" && (
                            <CardPaymentsTable payments={filteredCardPayments} accentColor={BANK_ACCENT} showAccount={cardShowAccount} />
                        )}

                        {view === "bank" && filteredCount > 0 && (
                            <TablePagination
                                placement="roster-top"
                                loading={loading}
                                accentColor={BANK_ACCENT}
                                page={txPage}
                                setPage={setTxPage}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                totalPages={totalPages}
                                totalCount={filteredCount}
                                pageSizes={PAGE_SIZES}
                            />
                        )}

                        {view === "bank" && (
                        <div className="table-container">
                            <table className="dashboard-table bank-table">
                                <thead>
                                    <tr>
                                        <th>{t("finapi.colDate")}</th>
                                        {multiAccount && <th>{t("finapi.colAccount")}</th>}
                                        <th>{t("finapi.colAmount")}</th>
                                        <th>{t("finapi.colCounterpart")}</th>
                                        <th>{t("finapi.colPurpose")}</th>
                                        <th>{t("finapi.colStatus")}</th>
                                        <th>{t("finapi.colAction")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageTxs.length === 0 && (
                                        <tr><td colSpan={multiAccount ? 7 : 6} className="finapi-table__empty">{t("finapi.noTransactions")}</td></tr>
                                    )}
                                    {pageTxs.map((tx) => {
                                        const chip = statusChip(tx);
                                        return (
                                            <tr
                                                key={tx.id}
                                                className={`${tx.matchedInvoiceIds.length === 1 ? "clickable-row " : ""}finapi-table__row${chip.cls === "reviewed" ? " finapi-table__row--dim" : ""}`}
                                                onClick={() => tx.matchedInvoiceIds.length === 1 && navigate(`/invoices/${tx.matchedInvoiceIds[0]}`)}
                                            >
                                                <td className="finapi-table__date">{fmtDate(tx.date)}</td>
                                                {multiAccount && <td className="finapi-table__owner">{tx.ownerDoctorName ?? "—"}</td>}
                                                <td className="finapi-table__amt finapi-table__amt--in">+ {fmt(tx.amount)}</td>
                                                <td className="finapi-table__counterpart">
                                                    {tx.counterpartName ?? t("finapi.unknownCounterpart")}
                                                    {isSumupPayout(tx) && (
                                                        <span className="finapi-table__sumup-chip" data-tooltip={t("sumup.payoutTooltip")}>
                                                            <TbCreditCard size={11} /> {t("sumup.payoutChip")}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="finapi-table__purpose" title={tx.purpose ?? undefined}>
                                                    {tx.purpose ?? "—"}
                                                    {tx.possibleDuplicateInvoice && (
                                                        <span className="finapi-table__dup-warning">
                                                            ⚠ {t("finapi.possibleDuplicate", { readableId: tx.possibleDuplicateInvoice.readableId })}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={`finapi-status-chip finapi-status-chip--${chip.cls}`}>{chip.label}</span>
                                                    {tx.matchedInvoices.length > 0 && (
                                                        <div className="finapi-table__matched-info">
                                                            {tx.matchedInvoices.map((inv) => (
                                                                <span key={inv.id}>
                                                                    {inv.readableId}{inv.patientName ? ` · ${inv.patientName}` : ""}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="finapi-table__actions" onClick={(e) => e.stopPropagation()}>
                                                    {tx.pending ? null : tx.ignored ? (
                                                        canReconcile && (
                                                            <button type="button" className="briefing-bank-row__action briefing-bank-row__action--ghost" disabled={ignoring === tx.id} onClick={() => handleUnignore(tx)}>
                                                                {t("finapi.unignore")}
                                                            </button>
                                                        )
                                                    ) : tx.matchedInvoiceIds.length > 0 ? (
                                                        <>
                                                            {tx.matchedBy !== "manual" && (
                                                                <span className="briefing-bank-row__action briefing-bank-row__action--done">
                                                                    <TbCircleCheck size={13} /> {t("finapi.autoMatched")}
                                                                </span>
                                                            )}
                                                            {canReconcile && (
                                                                <button type="button" className="briefing-bank-row__action briefing-bank-row__action--ghost" disabled={unmatching === tx.id} onClick={() => handleUnmatch(tx)}>
                                                                    <TbUnlink size={13} /> {t("finapi.unmatch")}
                                                                </button>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            {tx.possibleDuplicateInvoice && (
                                                                tx.possibleDuplicateInvoice.patientNotifiedAt ? (
                                                                    <span className="briefing-bank-row__action briefing-bank-row__action--done">
                                                                        <TbCircleCheck size={13} /> {t("finapi.patientNotified")}
                                                                    </span>
                                                                ) : canReconcile ? (
                                                                    <button type="button" className="briefing-bank-row__action briefing-bank-row__action--warning" disabled={notifyingDuplicate === tx.id} onClick={() => handleNotifyDuplicate(tx)}>
                                                                        {notifyingDuplicate === tx.id ? t("finapi.notifyingPatient") : t("finapi.notifyPatientDuplicate")}
                                                                    </button>
                                                                ) : null
                                                            )}
                                                            {canReconcile && (
                                                                <>
                                                                    <button type="button" className="briefing-bank-row__action" onClick={() => setMatchingTx(tx)}>
                                                                        <TbLink size={13} /> {t("finapi.manualMatch")}
                                                                    </button>
                                                                    <button type="button" className="briefing-bank-row__action briefing-bank-row__action--ghost" disabled={ignoring === tx.id} onClick={() => handleIgnore(tx)}>
                                                                        {t("finapi.ignore")}
                                                                    </button>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        )}

                    </>
                )}

                {!loading && !connected && (
                    <Panel className="finapi-panel">
                        <div className="briefing-bank-connect">
                            <div className="briefing-bank-connect__icon">
                                <MdOutlineAccountBalance size={22} />
                            </div>
                            <p className="briefing-bank-connect__text">{t("finapi.dashboardNudge")}</p>
                        </div>
                    </Panel>
                )}

                {/* Card payments don't need a bank connection — without one they get their own block. */}
                {!loading && !connected && cardAvailable && (
                    <section className="bank-card-payments">
                        <h2 className="bank-card-payments__title">
                            <TbCreditCard /> {t("sumup.tabCardPayments")} <span className="bank-card-payments__count">{cardPayments.length}</span>
                        </h2>
                        <CardPaymentsTable payments={filteredCardPayments} accentColor={BANK_ACCENT} showAccount={cardShowAccount} />
                    </section>
                )}

                {loading && (
                    <Panel className="finapi-panel">
                        <div className="briefing-bank-empty">
                            <span className="finapi-loading__spinner" />
                            {t("finapi.fetching")}
                        </div>
                    </Panel>
                )}

                {matchingTx && (
                    <BankMatchPopup
                        tx={matchingTx}
                        onAssign={handleAssign}
                        onMarkReviewed={handleMarkReviewed}
                        onClose={() => setMatchingTx(null)}
                    />
                )}
            </div>
        </div>
    );
}
